import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import specialtiesData from '../../data/specialties.json';
import './SpecialtyPage.css';

export default function SpecialtyPage() {
  const { specialtySlug } = useParams();
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandName = brand?.name || state.client?.name || 'Our Practice';
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || '';

  const specialty = specialtiesData.find(s => s.slug === specialtySlug);

  if (!specialty) {
    return (
      <div className="specialty-page">
        <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
          <p>{t('specialty.notFound', locale)}</p>
        </div>
      </div>
    );
  }

  // Brand-conditional: GA-only pages not available on non-GSO brands
  if (specialty.brandScope === 'gso' && brandKey !== 'gso') {
    return (
      <div className="specialty-page">
        <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
          <p>{t('specialty.gaOnly', locale)}</p>
          <a
            href={localizedPath('/services', locale)}
            className="glass-btn glass-btn-secondary"
            style={{ display: 'inline-block', marginTop: 24 }}
          >
            {t('specialty.backToServices', locale)}
          </a>
        </div>
      </div>
    );
  }

  const content = specialty.translations[locale] || specialty.translations.en;
  const canonicalUrl = `https://${baseDomain}/specialty/${specialtySlug}`;
  const pageTitle = content.metaTitle.replace('{{brandName}}', brandName);
  const pageDesc = content.desc;
  const ogImage = `https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: pageTitle,
    description: pageDesc,
    url: canonicalUrl,
  };

  return (
    <div className="specialty-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/specialty/${specialtySlug}`} />

      {/* Hero */}
      <section className="specialty-hero glass-dark">
        <div className="specialty-content container">
          {specialty.icon && (
            <span className="specialty-icon" aria-hidden="true">{specialty.icon}</span>
          )}
          <h1>{content.title}</h1>
          <p className="specialty-hero-desc">{content.desc}</p>
          <a
            href={localizedPath('/request-appt', locale)}
            className="glass-btn glass-btn-accent"
          >
            {content.cta}
          </a>
        </div>
      </section>

      {/* Sections */}
      <div className="specialty-content container specialty-main">
        {(content.sections || []).map((section, i) => (
          <section key={i} className="glass-panel specialty-section">
            <h2>{section.heading}</h2>
            <p>{section.body}</p>
          </section>
        ))}

        {/* Bottom CTA */}
        <div className="specialty-bottom-cta">
          <a
            href={localizedPath('/request-appt', locale)}
            className="glass-btn glass-btn-accent"
          >
            {content.cta}
          </a>
          <a
            href={localizedPath('/services', locale)}
            className="glass-btn glass-btn-secondary"
          >
            {t('specialty.backToServices', locale)}
          </a>
        </div>
      </div>
    </div>
  );
}
